// Единая дизайн-система приложения: палитра + токены темы antd.
// Источник — редизайн-макет (редакционный стиль: тёплый кремовый фон,
// тёмно-синий сайдбар, серифные заголовки Lora + гротеск Manrope для
// остального текста, двухцветные плашки статусов).

export const palette = {
  bg: '#F5F1E8',         // фон страницы (тёплый кремовый)
  cream: '#FAF7EF',      // фон панелей/заголовков таблиц
  surface: '#FFFFFF',    // карточки, модалки
  ink: '#171A1F',        // основной текст
  muted: '#6B6F76',      // вторичный текст
  border: '#E4DFCF',     // hairline-разделители
  navy: '#10182B',       // сайдбар / тёмные блоки
  blue: '#2C4BFF',       // основной акцент / primary-действия
  teal: '#0B5C52',       // «всё в порядке» / активно
  orange: '#C1560B',     // «требует внимания» / на проверке
  red: '#B23025',        // ошибки / критично
  sky: '#8FD6EC',        // светлый акцент, декоративные блоки
} as const;

// Шрифты: Lora (serif, заголовки h1/h3) + Manrope (sans, весь остальной
// текст/интерфейс) — подключены в public/index.html через Google Fonts.
export const fontHeading = "'Lora', Georgia, serif";
export const fontBody = "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Двухцветные плашки статусов (bg/fg) — заменяют одноцветный antd Tag на
// более спокойный, "редакционный" стиль. label — ТЕ ЖЕ тексты, что были
// в одноцветной версии, ничего не переименовано.
export const STATUS_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  active: { bg: '#E4F0EC', fg: palette.teal, label: 'Проверено' },
  manual_review: { bg: '#F7E8D9', fg: palette.orange, label: 'Требует проверки' },
  removed: { bg: '#ECEAE2', fg: palette.muted, label: 'Удалено' },
  duplicate: { bg: '#F7E8D9', fg: palette.orange, label: 'Дубль' },
};

export const TASK_STATUS_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  open: { bg: '#F6DEDA', fg: palette.red, label: 'Открыта' },
  in_progress: { bg: '#F7E8D9', fg: palette.orange, label: 'В работе' },
  resolved: { bg: '#E4F0EC', fg: palette.teal, label: 'Решена' },
  dismissed: { bg: '#ECEAE2', fg: palette.muted, label: 'Отклонена' },
};

export const FILE_STATUS_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  uploaded: { bg: '#ECEAE2', fg: palette.muted, label: 'Загружен' },
  parsing: { bg: '#F7E8D9', fg: palette.orange, label: 'Обрабатывается' },
  parsed: { bg: '#E4F0EC', fg: palette.teal, label: 'Обработан' },
  error: { bg: '#F6DEDA', fg: palette.red, label: 'Ошибка' },
};

// direct — ЮЛ само является объектом санкций (подавляющее большинство
// записей). indirect — ЮЛ идентифицировано только как компания, которой
// владеет санкционное ФЛ (само ФЛ отдельной карточкой не заводится, см.
// IndirectOwnershipPromoter на бэкенде) — данные владельца в indirect_owner_info.
export const INCLUSION_TYPE_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  direct: { bg: '#ECEAE2', fg: palette.muted, label: 'Прямое' },
  indirect: { bg: '#F7E8D9', fg: palette.orange, label: 'Косвенное' },
};

export const ROLE_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  admin: { bg: '#F6DEDA', fg: palette.red, label: 'Администратор' },
  editor: { bg: '#E1E6FF', fg: palette.blue, label: 'Редактор' },
  viewer: { bg: '#ECEAE2', fg: palette.muted, label: 'Наблюдатель' },
};

// Нейтральная плашка общего назначения (РФ-метка, счётчик дочерних ЮЛ и
// т.п.) — тот же оттенок, что и editor-роль/акцент-подсветка в макете.
export const ACCENT_PILL = { bg: '#E1E6FF', fg: palette.blue };
export const WARN_PILL = { bg: '#F7E8D9', fg: palette.orange };

export const antdTheme = {
  token: {
    colorPrimary: palette.blue,
    colorLink: palette.blue,
    colorLinkHover: palette.blue,
    colorBgLayout: palette.bg,
    colorBgContainer: palette.surface,
    colorText: palette.ink,
    colorTextHeading: palette.ink,
    colorTextSecondary: palette.muted,
    colorBorder: palette.border,
    colorBorderSecondary: palette.border,
    fontFamily: fontBody,
    fontSize: 14,
    borderRadius: 6,
    controlHeight: 34,
  },
  components: {
    Layout: {
      siderBg: palette.navy,
      headerBg: palette.surface,
      bodyBg: palette.bg,
      triggerBg: palette.navy,
    },
    Menu: {
      darkItemBg: palette.navy,
      darkItemSelectedBg: palette.blue,
      darkItemColor: 'rgba(255,255,255,0.72)',
      darkItemHoverColor: '#FFFFFF',
      darkItemSelectedColor: '#FFFFFF',
    },
    Card: {
      borderRadiusLG: 10,
      colorBorderSecondary: palette.border,
    },
    Button: {
      borderRadius: 6,
      fontWeight: 600,
      primaryShadow: 'none',
    },
    Table: {
      headerBg: palette.cream,
      headerColor: palette.ink,
      borderColor: palette.border,
      rowHoverBg: palette.cream,
    },
    Tag: {
      borderRadiusSM: 4,
    },
  },
};
